import {
  Database,
  Calendar,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Newspaper,
  Home,
  Share2,
  Plus,
  CheckSquare,
  Radio,
  Dna,
  MessageSquare,
  LifeBuoy,
  Bug,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useAuthStore, useUIStore, useTourStore, usePlanStore } from '@/stores';
import { isSubscriptionActive } from '@/lib/subscription';
import { canOpenSection } from '@/lib/premiumFeatures';
import { authClient } from '@/lib/auth-client';
import { env } from '@/lib/env';
import { openBugReport, isBugReportEnabled } from '@/lib/sentry';
import { BackgroundProcessIndicator } from '@/features/onboarding/components/BackgroundProcessIndicator';
import { SidebarPlanWidget } from '@/components/billing';
import liliLogo from '@/assets/0a921e3113ee22e2624d46430de694433d2cc716.png';
import liliLogoCollapsed from '@/assets/86c2ada6b17c96ded2885b14f5b84f812c24234e.png';

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onCreatePost?: () => void;
  /**
   * Show the descriptive nav-item hover tooltips (the on-hover feature copy).
   * Opt-in via the TopBar "Show tips" pill (LIL-377); `AppLayout` wires this to
   * `useTooltipStore().tipsEnabled`, which defaults to off. When the sidebar is
   * collapsed the label tooltip still shows regardless, since icon-only nav
   * needs it for usability. The `true` default here only applies when the prop
   * is omitted.
   */
  introTooltipsEnabled?: boolean;
}

type NavigationItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Descriptive copy shown on hover. */
  tooltip: string;
};

type NavigationSection = {
  title?: string;
  items: NavigationItem[];
};

const navigationSections: NavigationSection[] = [
  {
    items: [
      {
        id: 'dashboard',
        label: 'Home',
        icon: Home,
        tooltip: 'Your dashboard with recent posts, scheduled content, and quick actions.',
      },
    ],
  },
  {
    title: 'Create',
    items: [
      {
        id: 'chat',
        label: 'Chat',
        icon: MessageSquare,
        tooltip: 'Chat with LiLi to brainstorm, draft, and refine posts.',
      },
      {
        id: 'content-review',
        label: 'Review Queue',
        icon: CheckSquare,
        tooltip: 'Approve, edit, or reject posts LiLi has drafted for you.',
      },
      {
        id: 'calendar',
        label: 'Calendar',
        icon: Calendar,
        tooltip: 'See and reschedule everything queued across your channels.',
      },
    ],
  },
  {
    title: 'Research',
    items: [
      {
        id: 'knowledge-base',
        label: 'Knowledge Base',
        icon: Database,
        tooltip: 'Upload references LiLi uses to ground your content.',
      },
      {
        id: 'topic-trends',
        label: 'Social Listening',
        icon: Radio,
        tooltip: 'Track trending topics in your niche.',
      },
      {
        id: 'market-news',
        label: 'Market News',
        icon: Newspaper,
        tooltip: 'Curated market headlines for your industry.',
      },
    ],
  },
  {
    title: 'Manage',
    items: [
      {
        id: 'preferences',
        label: 'Brand DNA',
        icon: Dna,
        tooltip: 'Tune your tone, niche, and goals to drive every generated post.',
      },
      {
        id: 'channels',
        label: 'Channels',
        icon: Share2,
        tooltip: 'Connect or disconnect social accounts.',
      },
    ],
  },
];

export function Sidebar({
  currentPage,
  onNavigate,
  onCreatePost,
  introTooltipsEnabled = true,
}: SidebarProps) {
  const { sidebarCollapsed: collapsed, toggleSidebar: onToggleCollapse } = useUIStore();
  const { hasCompletedOnboarding } = useAuthStore();
  const isTourActive = useTourStore(s => s.status === 'active');
  const { data: session } = authClient.useSession();
  const [isOpen, setIsOpen] = useState(false);

  // Premium gating (LIL): after onboarding the shell is browsable, but premium
  // nav items show a lock and open the upgrade prompt instead of navigating.
  // Mirror FeatureGate's safety rules — don't flash a lock before the first
  // subscription read, and treat a failed read as unlocked (fail open).
  const { subscription, subscriptionError, hydrated: planHydrated } = usePlanStore();
  const subscribed = isSubscriptionActive(subscription);
  const isLocked = (id: string) => {
    if (!planHydrated || (subscriptionError && !subscribed)) return false;
    return !canOpenSection(subscription, id);
  };

  const handleNavClick = (item: NavigationItem) => {
    if (isLocked(item.id)) {
      // Contextual upgrade prompt (UpgradeListener) — keeps the user in place
      // rather than yanking them to the paywall on a single click.
      window.dispatchEvent(
        new CustomEvent('billing:feature-locked', { detail: { feature: item.label } })
      );
    } else {
      onNavigate(item.id);
    }
    setIsOpen(false);
  };
  const userName = session?.user?.name || 'User';
  const userEmail = session?.user?.email || '';
  const userImage = session?.user?.image || '';
  const userInitials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Descriptive hover tooltip for footer items (Help Center, Billing) — matches
  // the nav-item design (LIL-409). Gated like the nav tooltips: always shown when
  // collapsed (icon-only), otherwise opt-in via the "Show tips" pill.
  const showFooterTip = collapsed || introTooltipsEnabled;
  const withFooterTip = (
    trigger: ReactElement,
    TipIcon: LucideIcon,
    label: string,
    copy: string
  ) =>
    showFooterTip ? (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right">
          <div className="flex items-start gap-2.5">
            <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
              <TipIcon className="size-4" />
            </span>
            {collapsed ? (
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-900">{label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{copy}</div>
              </div>
            ) : (
              <span className="min-w-0">{copy}</span>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    ) : (
      trigger
    );

  return (
    <>
      {/* Mobile Menu Button — only while the drawer is closed; once open, the
          close control lives in the drawer header beside the logo so the two
          never overlap (LIL-553). The tour only targets this closed state. */}
      {!isOpen && (
        <Button
          variant="ghost"
          size="icon"
          data-tour="menu-toggle"
          aria-label="Open menu"
          className="fixed top-4 left-4 z-50 lg:hidden bg-white border border-border shadow-sm"
          onClick={() => setIsOpen(true)}
        >
          <Menu className="size-5" />
        </Button>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 h-dvh lg:h-full z-40
          ${collapsed ? 'lg:w-16' : 'lg:w-56'} w-56
          bg-white/70 backdrop-blur-2xl border-r border-white/20
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_3px_rgba(0,0,0,0.05)]
          before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/40 before:to-transparent before:pointer-events-none
          before:backdrop-saturate-150
        `}
      >
        <div className="flex flex-col h-full relative z-10">
          {/* Logo */}
          <div className="px-3 py-4 border-b border-white/20 backdrop-blur-sm">
            <div
              className={`flex items-center ${collapsed ? 'flex-col gap-3' : 'justify-between gap-2'}`}
            >
              {!collapsed && (
                <div className="flex items-center px-2">
                  <img src={liliLogo} alt="lili" className="h-7 w-auto" />
                </div>
              )}
              {collapsed && (
                <img src={liliLogoCollapsed} alt="lili" className="w-7 h-7 object-contain" />
              )}
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex text-[#6B7280] hover:text-[#111827] hover:bg-[#E5E7EB] h-7 w-7"
                onClick={onToggleCollapse}
              >
                {collapsed ? (
                  <ChevronRight className="size-3.5" />
                ) : (
                  <ChevronLeft className="size-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close menu"
                className="lg:hidden text-[#6B7280] hover:text-[#111827] hover:bg-[#E5E7EB] h-7 w-7"
                onClick={() => setIsOpen(false)}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 px-2 py-3 overflow-y-auto overscroll-contain">
            {/* Background automation indicator */}
            <BackgroundProcessIndicator />

            {/* Create New Button */}
            {!collapsed && (
              <div className="mb-4 px-0.5">
                {(() => {
                  const createButton = (
                    <div data-tour="create-new">
                      <Button
                        onClick={() => {
                          onCreatePost?.();
                          setIsOpen(false);
                        }}
                        disabled={!hasCompletedOnboarding}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="size-4 mr-2" />
                        Create New
                      </Button>
                    </div>
                  );
                  // Only show the descriptive hover tooltip when tips are opted
                  // in (LIL-377), matching the nav items. When onboarding isn't
                  // complete, always show it so the disabled reason is explained.
                  const showTooltip = introTooltipsEnabled || !hasCompletedOnboarding;
                  return showTooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{createButton}</TooltipTrigger>
                      <TooltipContent side="right">
                        {!hasCompletedOnboarding
                          ? 'Complete onboarding to create content'
                          : 'Start a new post'}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    createButton
                  );
                })()}
              </div>
            )}
            {collapsed && (
              <div className="mb-4 px-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div data-tour="create-new">
                      <Button
                        onClick={() => {
                          onCreatePost?.();
                          setIsOpen(false);
                        }}
                        disabled={!hasCompletedOnboarding}
                        size="icon"
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="size-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {!hasCompletedOnboarding
                      ? 'Complete onboarding to create content'
                      : 'Create New Post'}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            <div className="space-y-4">
              {navigationSections.map((section, sectionIndex) => (
                <div key={section.title || sectionIndex}>
                  {section.title && !collapsed && (
                    <div className="px-2.5 mb-2">
                      <span className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
                        {section.title}
                      </span>
                    </div>
                  )}
                  {collapsed && section.title && sectionIndex > 0 && (
                    <div className="h-px bg-[#E5E7EB] mx-2 mb-1" />
                  )}
                  <ul className="space-y-0.5">
                    {section.items.map(item => {
                      const Icon = item.icon;
                      const isActive = !isTourActive && currentPage === item.id;
                      const locked = isLocked(item.id);
                      const navButton = (
                        <button
                          data-tour={`nav-${item.id}`}
                          onClick={() => handleNavClick(item)}
                          className={`
                            w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
                            transition-colors text-sm
                            ${collapsed ? 'justify-center' : ''}
                            ${
                              isActive
                                ? 'bg-white text-[#111827] shadow-sm'
                                : 'text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
                            }
                          `}
                        >
                          <Icon className="size-4 flex-shrink-0" />
                          {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                          {!collapsed && locked && (
                            <Lock className="size-3.5 flex-shrink-0 text-[#9CA3AF]" />
                          )}
                        </button>
                      );
                      // Collapsed nav always needs a tooltip (icon-only labels).
                      // Expanded nav only shows the descriptive hover tooltip on
                      // the user's first visit, then drops it entirely (LIL-355).
                      const showTooltip = collapsed || introTooltipsEnabled;
                      return (
                        <li key={item.id}>
                          {showTooltip ? (
                            <Tooltip>
                              <TooltipTrigger asChild>{navButton}</TooltipTrigger>
                              <TooltipContent side="right">
                                {/* Card, shadow and the purple→indigo accent bar come
                                    from the shared TooltipContent (LIL-409). Here we
                                    only add the hovered section's own icon in a tinted
                                    chip, beside the descriptive copy. */}
                                <div className="flex items-start gap-2.5">
                                  <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                                    <Icon className="size-4" />
                                  </span>
                                  {collapsed ? (
                                    <div className="min-w-0">
                                      <div className="text-xs font-semibold text-gray-900">
                                        {item.label}
                                      </div>
                                      {introTooltipsEnabled && (
                                        <div className="text-[11px] text-gray-500 mt-0.5">
                                          {item.tooltip}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="min-w-0">{item.tooltip}</span>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            navButton
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          {/* Footer with Billing and User Info */}
          {!collapsed && (
            <div className="border-t border-[#E5E7EB]">
              {/* Plan badge + AI-credit usage meter + 80% warning (LIL-341) */}
              <SidebarPlanWidget
                collapsed={false}
                onNavigateBilling={() => {
                  onNavigate('billing');
                  setIsOpen(false);
                }}
              />
              {/* Help Center (external link to tawk.to KB) */}
              {env.HELP_CENTER_URL && (
                <div className="px-2 pt-2 space-y-0.5">
                  {withFooterTip(
                    <a
                      href={env.HELP_CENTER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md transition-colors text-sm text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]"
                    >
                      <LifeBuoy className="size-4 flex-shrink-0" />
                      <span>Help Center</span>
                    </a>,
                    LifeBuoy,
                    'Help Center',
                    'Browse guides and reach our support team.'
                  )}
                </div>
              )}
              {/* Report a bug (opens the Sentry feedback form) */}
              {isBugReportEnabled() && (
                <div className="px-2 pt-2 space-y-0.5">
                  <button
                    onClick={() => openBugReport()}
                    className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md transition-colors text-sm text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]"
                  >
                    <Bug className="size-4 flex-shrink-0" />
                    <span>Report a bug</span>
                  </button>
                </div>
              )}
              {/* Billing */}
              <div className="px-2 py-2 space-y-0.5">
                {withFooterTip(
                  <button
                    onClick={() => {
                      onNavigate('billing');
                      setIsOpen(false);
                    }}
                    className={`
                    w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md
                    transition-colors text-sm
                    ${
                      !isTourActive && currentPage === 'billing'
                        ? 'bg-white text-[#111827] shadow-sm'
                        : 'text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
                    }
                  `}
                  >
                    <CreditCard className="size-4 flex-shrink-0" />
                    <span>Billing & Usage</span>
                  </button>,
                  CreditCard,
                  'Billing & Usage',
                  'Manage your plan, payments, and usage.'
                )}
              </div>
              {/* User Info - Clickable to Profile */}
              <div className="px-2 pb-3">
                <button
                  onClick={() => {
                    onNavigate('profile');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-[#E5E7EB] transition-colors"
                >
                  <Avatar className="size-8 shrink-0">
                    {userImage && <AvatarImage src={userImage} alt={userName} />}
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-medium">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-[#111827] truncate">{userName}</p>
                    <p className="text-xs text-[#6B7280] truncate">{userEmail}</p>
                  </div>
                </button>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="border-t border-[#E5E7EB]">
              {/* Plan badge / usage cue (LIL-341) */}
              <SidebarPlanWidget
                collapsed
                onNavigateBilling={() => {
                  onNavigate('billing');
                  setIsOpen(false);
                }}
              />
              {/* Help Center icon (external link to tawk.to KB) */}
              {env.HELP_CENTER_URL && (
                <div className="px-2 pt-2 space-y-0.5">
                  {withFooterTip(
                    <a
                      href={env.HELP_CENTER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center px-2.5 py-1.5 rounded-md transition-colors text-sm text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]"
                    >
                      <LifeBuoy className="size-4 flex-shrink-0" />
                    </a>,
                    LifeBuoy,
                    'Help Center',
                    'Browse guides and reach our support team.'
                  )}
                </div>
              )}
              {/* Report a bug icon (opens the Sentry feedback form) */}
              {isBugReportEnabled() && (
                <div className="px-2 pt-2 space-y-0.5">
                  <button
                    onClick={() => openBugReport()}
                    className="w-full flex items-center justify-center px-2.5 py-1.5 rounded-md transition-colors text-sm text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]"
                    title="Report a bug"
                  >
                    <Bug className="size-4 flex-shrink-0" />
                  </button>
                </div>
              )}
              {/* Billing icon */}
              <div className="px-2 py-2 space-y-0.5">
                {withFooterTip(
                  <button
                    onClick={() => {
                      onNavigate('billing');
                      setIsOpen(false);
                    }}
                    className={`
                    w-full flex items-center justify-center px-2.5 py-1.5 rounded-md
                    transition-colors text-sm
                    ${
                      !isTourActive && currentPage === 'billing'
                        ? 'bg-white text-[#111827] shadow-sm'
                        : 'text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
                    }
                  `}
                  >
                    <CreditCard className="size-4 flex-shrink-0" />
                  </button>,
                  CreditCard,
                  'Billing & Usage',
                  'Manage your plan, payments, and usage.'
                )}
              </div>
              {/* Collapsed User Avatar */}
              <div className="px-2 pb-3">
                <button
                  onClick={() => {
                    onNavigate('profile');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-center py-1.5 rounded-md hover:bg-[#E5E7EB] transition-colors"
                  title={userName}
                >
                  <Avatar className="size-8">
                    {userImage && <AvatarImage src={userImage} alt={userName} />}
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-medium">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
